import * as R from 'ramda'
import { Story, StoryPeerInfo, StoryPeerType } from '@weblite/ts-action/story'
import { getState, RootState } from '$setup/redux'
import {
  ADD_STORY_TO_RIBBON,
  ADD_STORY_TO_STORIES,
  CREATE_UPLOADING_DATA,
  DELETE_STORY_FROM_RIBBON,
  DELETE_STORY_FROM_STORIES,
  DELETE_UPLOADING_DATA,
  dispatchAddStoryToRibbon,
  dispatchAddStoryToStories,
  dispatchCreateUploadingData,
  dispatchDeleteStoryFromRibbon,
  dispatchDeleteStoryFromStories,
  dispatchDeleteUploadingData,
  dispatchSetRibbonStoryData,
  dispatchSetStoryData,
  dispatchUpdateRibbonStoryViewedAt,
  dispatchUpdateUploadingData,
  SET_RIBBON_DATA,
  SET_STORY_DATA,
  UPDATE_RIBBON_STORY_VIEWED_AT,
  UPDATE_UPLOADING_DATA,
} from '$logic/story/actions'
import {
  AddStoryToRibbonPayload,
  AddStoryToStoriesPayload,
  StoryRibbon,
  StoryRibbonPriority,
  UpdateUploadData,
  UploadingStory,
} from './types'

export interface StoryStorageState {
  stories: Record<
    StoryPeerType,
    Record<
      string, // peerId
      Story[]
    >
  >
  serverStoryRibbons: StoryRibbon[]
  uploadingStories: Record<
    string, // uploadId
    UploadingStory
  >
}

const SERVER_STORY_RIBBONS = 'serverStoryRibbons'

const initialState: StoryStorageState = {
  stories: { CHANNEL: {}, GROUP: {}, USER: {} },
  serverStoryRibbons: [],
  uploadingStories: {},
}

// reducers
const reducers = {
  [SET_STORY_DATA]: (
    state: StoryStorageState,
    data: { peerId: string; peerType: StoryPeerType; stories: Story[] },
  ) => ({
    ...state,
    stories: {
      ...state.stories,
      [data.peerType]: {
        ...state.stories[data.peerType],
        [data.peerId]: data.stories,
      },
    },
  }),

  [ADD_STORY_TO_RIBBON]: (
    state: StoryStorageState,
    data: AddStoryToRibbonPayload,
  ) => {
    const { peerId, peerType, storyId, lastStoryAt, priority } = data
    const ribbonStoryIndex = state.serverStoryRibbons.findIndex(
      storyRibbon =>
        storyRibbon.peerId === peerId && storyRibbon.peerType === peerType,
    )
    if (ribbonStoryIndex === -1) {
      return R.set(
        R.lensPath([SERVER_STORY_RIBBONS]),
        R.prepend(
          {
            peerId,
            peerType,
            stories: [{ id: storyId }],
            lastStoryAt,
            priority,
          } as StoryRibbon,
          state.serverStoryRibbons,
        ),
        state,
      )
    }

    return R.set(
      R.lensPath([SERVER_STORY_RIBBONS, ribbonStoryIndex]),
      {
        ...state[SERVER_STORY_RIBBONS][ribbonStoryIndex],
        stories: R.append(
          { id: storyId },
          state.serverStoryRibbons[ribbonStoryIndex].stories,
        ),
        lastStoryAt,
        priority,
      },
      state,
    )
  },

  [DELETE_STORY_FROM_RIBBON]: (
    state: StoryStorageState,
    payload: AddStoryToStoriesPayload,
  ) => {
    const { peerId, peerType, storyId } = payload
    const ribbonIndex = state.serverStoryRibbons.findIndex(
      ribbon => ribbon.peerId === peerId && ribbon.peerType === peerType,
    )
    if (ribbonIndex === -1) return state
    const storyIndex = state.serverStoryRibbons[ribbonIndex].stories.findIndex(
      story => story.id === storyId,
    )
    if (storyIndex === -1) return state
    const newStoryList = R.remove(
      storyIndex,
      1,
      state.serverStoryRibbons[ribbonIndex].stories,
    )
    if (newStoryList.length)
      return R.set(
        R.lensPath([SERVER_STORY_RIBBONS, ribbonIndex, 'stories']),
        newStoryList,
        state,
      )
    return R.set(
      R.lensPath([SERVER_STORY_RIBBONS]),
      R.remove(ribbonIndex, 1, state.serverStoryRibbons),
      state,
    )
  },

  [ADD_STORY_TO_STORIES]: (state: StoryStorageState, data: Story) => {
    const { peerId, peerType } = data
    return R.set(
      R.lensPath(['stories', peerType, peerId]),
      R.append(data, state.stories[peerType][peerId] || []),
      state,
    )
  },

  [DELETE_STORY_FROM_STORIES]: (
    state: StoryStorageState,
    payload: AddStoryToStoriesPayload,
  ) => {
    const { peerId, peerType, storyId } = payload

    const storyIndex = state.stories[peerType][peerId].findIndex(
      story => story.id === storyId,
    )
    if (storyIndex === -1) return state
    const newStories = R.remove(storyIndex, 1, state.stories[peerType][peerId])
    if (newStories.length)
      return R.set(R.lensPath(['stories', peerType, peerId]), newStories, state)
    return R.dissocPath(['stories', peerType, peerId], state)
  },

  [SET_RIBBON_DATA]: (
    state: StoryStorageState,
    { replace, payload }: { replace: boolean; payload: StoryRibbon[] },
  ) => {
    return R.set(
      R.lensProp('serverStoryRibbons'),
      replace ? payload : R.concat(state.serverStoryRibbons, payload),
      state,
    )
  },

  [CREATE_UPLOADING_DATA]: (
    state: StoryStorageState,
    data: UploadingStory,
  ) => ({
    ...state,
    uploadingStories: {
      ...state.uploadingStories,
      [data.uploadId]: data,
    },
  }),
  [UPDATE_UPLOADING_DATA]: (
    state: StoryStorageState,
    data: UpdateUploadData,
  ) => ({
    ...state,
    uploadingStories: {
      ...state.uploadingStories,
      [data.uploadId]: { ...state.uploadingStories[data.uploadId], ...data },
    },
  }),
  [DELETE_UPLOADING_DATA]: (
    state: StoryStorageState,
    data: { uploadId: string },
  ) => R.dissocPath(['uploadingStories', data.uploadId], state),

  [UPDATE_RIBBON_STORY_VIEWED_AT]: (
    state: StoryStorageState,
    data: { peerId: string; peerType: StoryPeerType; storyId: string },
  ) => {
    const ribbonIndex = state.serverStoryRibbons.findIndex(
      ribbon =>
        ribbon.peerId === data.peerId && ribbon.peerType === data.peerType,
    )

    if (ribbonIndex === -1) return state

    const storyIndex = state.serverStoryRibbons[ribbonIndex].stories.findIndex(
      story => story.id === data.storyId,
    )

    return R.set(
      R.lensPath([
        SERVER_STORY_RIBBONS,
        ribbonIndex,
        'stories',
        storyIndex,
        'viewedAt',
      ]),
      new Date().toString(),
      state,
    )
  },

  // [UPDATE_STORY_RIBBON_VIEWED_ALL]: (
  //   state: StoryStorageState,
  //   data: { peerId: string; peerType: StoryPeerType; viewedAll: boolean },
  // ) => {
  //   const ribbonIndex = state.serverStoryRibbons.findIndex(
  //     ribbon =>
  //       ribbon.peerId === data.peerId && ribbon.peerType === data.peerType,
  //   )
  //
  //   if (ribbonIndex === -1) return state
  //
  //   return R.set(
  //     R.lensPath([SERVER_STORY_RIBBONS, ribbonIndex, 'viewedAll']),
  //     data.viewedAll,
  //     state,
  //   )
  // },
}

export const storiesStorage = {
  setStoryData: (peerId: string, peerType: StoryPeerType, stories: Story[]) =>
    dispatchSetStoryData({ peerId, peerType, stories }),
  getStoryData: () =>
    (getState().main.story as StoryStorageState | undefined)?.stories,
  getStoriesByPeerIdAndPeerType: (peerId: string, peerType: StoryPeerType) =>
    (getState().main.story as StoryStorageState | undefined)?.stories[peerType][
      peerId
    ],

  setServerStoryRibbonsData: (
    serverStoryRibbons: StoryRibbon[],
    replace = false,
  ) => dispatchSetRibbonStoryData({ payload: serverStoryRibbons, replace }),
  getServerStoryRibbonsData: () =>
    (getState().main.story as StoryStorageState | undefined)
      ?.serverStoryRibbons as StoryRibbon[],

  createUploadingData: (data: UploadingStory) =>
    dispatchCreateUploadingData(data),
  updateUploadingData: (data: UpdateUploadData) =>
    dispatchUpdateUploadingData(data),
  deleteUploadingData: (uploadId: string) =>
    dispatchDeleteUploadingData(uploadId),
  getUploadingData: (uploadId: string) => {
    return (getState().main.story as StoryStorageState | undefined)
      ?.uploadingStories?.[uploadId]
  },

  updateRibbonStoryViewedAt: (
    peerId: string,
    peerType: StoryPeerType,
    storyId: string,
  ) => {
    dispatchUpdateRibbonStoryViewedAt(peerId, peerType, storyId)
  },

  // updateStoryRibbonViewedAll: (
  //   peerId: string,
  //   peerType: StoryPeerType,
  //   viewedAll: boolean,
  // ) => {
  //   dispatchUpdateStoryRibbonViewedAll(peerId, peerType, viewedAll)
  // },

  addStoriesToRibbon: (data: AddStoryToRibbonPayload) => {
    dispatchAddStoryToRibbon(data)
  },
  deleteStoryFromRibbon: (data: AddStoryToStoriesPayload) => {
    dispatchDeleteStoryFromRibbon(data)
  },
  addStoryToStories: (data: Story) => {
    dispatchAddStoryToStories(data)
  },
  deleteStoryFromStories: (data: AddStoryToStoriesPayload) => {
    dispatchDeleteStoryFromStories(data)
  },
}

export const uploadingStoriesView = (state: RootState) =>
  state.main.story.uploadingStories

export const storiesView = (state: RootState) => state.main.story.stories
export const storiesViewByPeerId = (
  peerId: string,
  peerType: StoryPeerType,
) => (state: RootState) => state.main.story.stories[peerType][peerId]
export const serverRibbonStoriesView = (state: RootState) =>
  state.main.story.serverStoryRibbons

export const storiesReducer = (
  state = initialState,
  {
    type,
    payload,
  }: {
    type: keyof typeof reducers
    payload: any
  },
) => (reducers[type] ? reducers[type](state, payload) : state)
